import {
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { notifyMutationError } from "@/lib/repository-utils";
import type { PerformanceCastNestedType } from "@/pages/performances/schemas/performance.edit.form";
import type { PlayCastNestedType } from "@/pages/plays/schemas/play.nested.form";

export interface PlaySummary {
  id: string;
  title: string;
  originalTitle?: string;
  media?: { photos?: Array<{ url?: string }> };
}

/**
 * Play repository — search over the plays Typesense explore endpoint via Refine
 * getList, mirroring useMovieRepository. getPlay/updatePlay and
 * getPerformance/updatePerformance mirror useMovieRepository.update.
 */
export const useTheaterRepository = () => {
  const { getList, getOne } = useDataProvider()();
  const { mutateAsync: updatePlayAsync } = useUpdate({ resource: "plays" });
  const { mutateAsync: updatePerformanceAsync } = useUpdate({
    resource: "performances",
  });
  const { edit } = useNavigation();
  const { open } = useNotification();

  async function search(query: string): Promise<PlaySummary[]> {
    if (!query) return [];
    try {
      const response = await getList({
        resource: "plays",
        pagination: { currentPage: 0, pageSize: 25 },
        filters: [],
        meta: {
          sortBy: "_text_match:desc",
          query,
          queryBy: "title,originalTitle",
        },
      });
      return response.data.map(
        (hit: any) => hit?.document ?? hit,
      ) as PlaySummary[];
    } catch (ex) {
      console.error("plays search failed due to:", ex);
      open?.({
        message: `plays search failed due to ${toHumanErrorMessage(ex)}`,
        type: "error",
      });
      return [];
    }
  }

  async function getPlay(id: string): Promise<unknown> {
    return getOne({ id, resource: "plays" });
  }

  async function getPerformance(id: string): Promise<unknown> {
    return getOne({ id, resource: "performances" });
  }

  /**
   * Partial update against `UpdatePlayCommand`. Three translations the command needs:
   * `genres` objects become `genreIds`, cast entries become `PlayCastRequestDto` (exactly
   * one of personId|name, or the handler fails the whole update), and an EMPTY slug is
   * dropped — AutoForm deletes an emptied key, and a re-added `""` makes the handler
   * answer "Slug cannot be empty" (ims#95).
   */
  async function updatePlay(values: Record<string, any>) {
    const payload: Record<string, unknown> = { id: values.id };
    for (const [key, value] of Object.entries(values)) {
      if (key === "id" || key === "genres" || key === "cast") continue;
      if (key === "slug" && !String(value ?? "").trim()) continue;
      payload[key] = value;
    }
    if (values.genres !== undefined) {
      payload.genreIds = (values.genres ?? []).map((g: { id: string }) => g.id);
    }
    if (values.cast !== undefined) {
      payload.cast = (values.cast ?? []).map(toPlayCastRequest);
    }

    const response = await updatePlayAsync?.(
      { id: values.id, values: payload },
      {
        onSuccess: () => {
          edit("plays", values.id, "replace");
          open?.({
            type: "success",
            message: "Successfully updated play",
            description: `You updated ${values.title ?? "a play"}`,
          });
        },
        onError: (error) => {
          console.error("Failed to update play with values", values, error);
          notifyMutationError(open, "Update play request", error);
        },
      },
    );
    return response?.data.id?.toString();
  }

  /**
   * Partial update against `UpdatePerformanceCommand`, which today accepts only
   * startDate/cast/status/tags/hide. `cast` is the raw `PerformanceCastDbDto`, whose
   * personId and name are both REQUIRED — so an unlinked entry cannot be represented and
   * is dropped here rather than 400ing the whole save.
   */
  async function updatePerformance(values: Record<string, any>) {
    const payload: Record<string, unknown> = { id: values.id };
    for (const [key, value] of Object.entries(values)) {
      if (key === "id" || key === "cast") continue;
      payload[key] = value;
    }
    if (values.cast !== undefined) {
      payload.cast = (values.cast ?? [])
        .filter((c: PerformanceCastNestedType) => !!c.personId && !!c.name)
        .map((c: PerformanceCastNestedType) => ({
          personId: c.personId,
          name: c.name,
          role: c.role ?? null,
          department: c.department || "Acting",
          // `PerformanceCastDbDto` is written straight through (a whole-array
          // replace via `.Set(p => p.Cast, ...)`), so a member's order/photo
          // must pass through unchanged or the save nulls them out. Omit the
          // key when the entry never had it; keep an explicit null as null.
          ...(c.order !== undefined ? { order: c.order } : {}),
          ...(c.photo !== undefined ? { photo: c.photo } : {}),
        }));
    }

    const response = await updatePerformanceAsync?.(
      { id: values.id, values: payload },
      {
        onSuccess: () => {
          edit("performances", values.id, "replace");
          open?.({
            type: "success",
            message: "Successfully updated performance",
          });
        },
        onError: (error) => {
          console.error("Failed to update performance", values, error);
          notifyMutationError(open, "Update performance request", error);
        },
      },
    );
    return response?.data.id?.toString();
  }

  return { search, getPlay, updatePlay, getPerformance, updatePerformance };
};

/** `PlayCastRequestDto`: exactly one of personId | name, never both. */
function toPlayCastRequest(c: PlayCastNestedType) {
  const linked = !!c.personId;
  return {
    personId: linked ? c.personId : null,
    name: linked ? null : (c.name ?? ""),
    // A name-only credit's locale and Latin form; the backend ignores both on a linked credit,
    // and dropping them here would erase Tina's values on every curator save.
    nameLocale: linked ? null : c.nameLocale?.trim() || null,
    latinName: linked ? null : c.latinName?.trim() || null,
    characterName: c.characterName ?? null,
    department: c.department || "Acting",
  };
}
